import { Component } from '@theme/component';
import { MA_CITIES } from './cod-cities.js';

function b64urlEncode(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * COD order form component: popup/embedded, cart/product/preset items,
 * client-side totals preview, submit to the Cloudflare worker.
 */
class CodOrderForm extends Component {
  #config = null;
  #abort = null;
  #presetVariant = null;
  #items = null;
  #submitting = false;
  #lastOrder = null;
  #idemKey = null;
  #eventId = null;
  #audioCtx = null;
  #io = null;

  connectedCallback() {
    super.connectedCallback();
    const raw = this.querySelector('script[data-cod-config]');
    if (!raw) return;
    try {
      this.#config = JSON.parse(raw.textContent);
    } catch {
      return;
    }
    this.#abort = new AbortController();
    const signal = this.#abort.signal;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('input', this.#onInput, { signal });

    const dialog = this.querySelector('[data-cod-dialog]');
    if (dialog) {
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) this.close();
      }, { signal });
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    }, { signal });

    if (this.dataset.mode === 'popup') {
      if (this.#config.openHash && location.hash === '#cod-open') this.open();
      if (this.#config.autoOpenDelay > 0) {
        setTimeout(() => this.open(), this.#config.autoOpenDelay * 1000);
      }
    }

    this.#formatPackPrices();
    this.#renderProvince();
    this.#refreshCart();
    this.#syncCheckoutVisibility();
    if (this.dataset.mode !== 'popup') this.#watchFormVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#io?.disconnect();
    this.#io = null;
    document.documentElement.classList.remove('cod-form-view');
  }

  /* ---------- public API ---------- */

  open(opts = {}) {
    if (opts.variantId) {
      this.#presetVariant = opts.variantId;
      const card = this.querySelector(`input[data-cod-package][value="${opts.variantId}"]`);
      if (card) card.checked = true;
    } else if (!this.#presetVariant) {
      const card = this.querySelector('input[data-cod-package]:checked');
      if (card) this.#presetVariant = card.value;
      else if (this.#config?.productVariant) this.#presetVariant = `gid://shopify/ProductVariant/${this.#config.productVariant}`;
    }
    const dialog = this.querySelector('[data-cod-dialog]');
    if (this.dataset.mode === 'popup') {
      if (!dialog) return;
      dialog.hidden = false;
      document.documentElement.classList.add('cod-locked');
      const focusTarget = dialog.querySelector('input, select, textarea, button');
      focusTarget?.focus();
    } else {
      const form = this.querySelector('[data-cod-view="form"]');
      form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const firstEmpty =
        Array.from(this.querySelectorAll('[data-cod-view="form"] input[data-cod-required]')).find(
          (el) => !String(el.value || '').trim(),
        ) || this.querySelector('[data-cod-view="form"] input[data-cod-required]');
      if (firstEmpty) {
        setTimeout(() => {
          try {
            firstEmpty.focus({ preventScroll: true });
          } catch {
            firstEmpty.focus();
          }
        }, 350);
      }
    }
    this.#showForm();
    if (this.#presetVariant) this.#refreshSingleItem();
    this.#syncCheckoutVisibility();
    this.#track('begin_checkout', {});
  }

  close() {
    const dialog = this.querySelector('[data-cod-dialog]');
    if (this.dataset.mode !== 'popup' || !dialog || dialog.hidden) return;
    dialog.hidden = true;
    document.documentElement.classList.remove('cod-locked');
  }

  /* ---------- events ---------- */

  #onDocumentClick = (event) => {
    const trigger = event.target.closest('[data-cod-open]');
    if (trigger && !event.defaultPrevented) {
      event.preventDefault();
      const variantId = trigger.dataset.codVariant || trigger.dataset.dpPackage || null;
      this.open({ variantId });
      return;
    }
    const closer = event.target.closest('[data-cod-close]');
    if (closer) {
      event.preventDefault();
      this.close();
    }
  };

  #onClick = (event) => {
    if (event.target.closest('[data-cod-submit]')) {
      event.preventDefault();
      this.#submit();
    }
  };

  #onChange = (event) => {
    const t = event.target;
    if (t instanceof HTMLInputElement && t.matches('[data-cod-package]') && t.checked) {
      this.#presetVariant = t.value;
      this.#playSelectSound();
      this.#refreshSingleItem();
      return;
    }
    if (t instanceof HTMLInputElement && t.matches('[data-cod-rate]')) {
      this.#renderSummary();
    }
  };

  #onInput = (event) => {
    const t = event.target;
    const isTextField =
      t instanceof HTMLTextAreaElement ||
      (t instanceof HTMLInputElement && ['text', 'tel', 'email', 'number', 'search'].includes(t.type));
    if (!isTextField) return;
    this.#playKeySound();
  };

  #watchFormVisibility() {
    if (typeof IntersectionObserver !== 'function') return;
    const form = this.querySelector('[data-cod-view="form"]');
    if (!form) return;
    this.#io = new IntersectionObserver(
      (entries) => {
        document.documentElement.classList.toggle('cod-form-view', entries[0].isIntersecting);
      },
      { threshold: 0.2 },
    );
    this.#io.observe(form);
  }

  #playSelectSound() {
    if (this.#config?.selectionSound === false) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.#audioCtx ||= new Ctx();
      const ctx = this.#audioCtx;
      if (ctx.state === 'suspended') ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(920, t);
      osc.frequency.exponentialRampToValueAtTime(520, t + 0.08);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.11);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.12);
    } catch {
      return;
    }
  }

  #playKeySound() {
    if (this.#config?.selectionSound === false) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.#audioCtx ||= new Ctx();
      const ctx = this.#audioCtx;
      if (ctx.state === 'suspended') ctx.resume();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const freq = 1250 + Math.random() * 260;
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.55, t + 0.03);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.12, t + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.035);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.04);
    } catch {
      return;
    }
  }

  #onSubmit = (event) => {
    event.preventDefault();
    this.#submit();
  };

  /* ---------- views ---------- */

  #showForm() {
    const form = this.querySelector('[data-cod-view="form"]');
    const success = this.querySelector('[data-cod-view="success"]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
  }

  #isUrlThankYou() {
    return this.#config.thankyouMode === 'url' && Boolean(this.#config.thankyouUrl);
  }

  #thankYouUrl() {
    const raw = this.#config.thankyouUrl;
    if (!raw) return '/';
    let url;
    try {
      url = new URL(raw, location.origin);
    } catch {
      return raw;
    }
    if (url.origin !== location.origin) return url.href;

    const root = String(window.Shopify?.routes?.root || '/').replace(/\/+$/, '');
    if (root && url.pathname.startsWith('/pages/') && !url.pathname.startsWith(`${root}/`)) {
      url.pathname = `${root}${url.pathname}`;
    }

    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview && !url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', preview);
    }

    return `${url.pathname}${url.search}${url.hash}`;
  }

  #showSuccess(orderName, total) {
    const form = this.querySelector('[data-cod-view="form"]');
    const success = this.querySelector('[data-cod-view="success"]');
    if (form) form.hidden = true;
    if (success) success.hidden = false;
    const nameEl = this.querySelector('[data-cod-order-name]');
    const totalEl = this.querySelector('[data-cod-order-total]');
    if (nameEl) nameEl.textContent = orderName;
    if (totalEl) totalEl.textContent = this.#money(total * 100);
  }

  #saveLastOrderSummary(body, payload, { duplicate = false } = {}) {
    const name = String(this.querySelector('[name="name"]')?.value || '').trim();
    const items = (this.#items || []).slice(0, 10).map((item) => {
      const quantity = Number(item.quantity) || 1;
      const lineTotal = Math.max(0, Number(item.price) || 0);
      return {
        id: String(item.variantId || '').split('/').pop(),
        t: item.title || '',
        q: quantity,
        p: Math.round(lineTotal / 100),
        up: Math.round(lineTotal / quantity) / 100,
      };
    });
    const summary = {
      firstName: name.split(/\s+/)[0] || '',
      orderName: String(body.orderName || ''),
      total: duplicate ? 0 : Number(body.total) || 0,
      currency: this.#config.currency || 'MAD',
      locale: this.#config.locale === 'fr' ? 'fr' : 'ar',
      city: String(this.querySelector('[name="city"]')?.value || '').trim(),
      items: duplicate ? [] : items,
      eventId: payload?.eventId || '',
      pixels: this.#config.pixels || {},
      duplicate: Boolean(duplicate),
      ts: Date.now(),
    };
    try {
      sessionStorage.setItem('cod:lastOrder', JSON.stringify(summary));
    } catch {
      /* storage unavailable: summary is a bonus, the redirect must not fail on it */
    }
    return summary;
  }

  #hashSummary(summary) {
    return {
      orderName: summary.orderName,
      total: summary.total,
      currency: summary.currency,
      locale: summary.locale,
      eventId: summary.eventId,
      pixels: summary.pixels,
      duplicate: summary.duplicate,
      ts: summary.ts,
    };
  }

  #redirectToThankYou(body, payload, options = {}) {
    const summary = this.#saveLastOrderSummary(body, payload, options);
    const url = new URL(this.#thankYouUrl(), location.origin);
    url.hash = 'o=' + b64urlEncode(JSON.stringify(this.#hashSummary(summary)));
    location.assign(url.href);
  }

  /* ---------- province + city ---------- */

  #renderProvince() {
    const select = this.querySelector('select[name="province"]');
    if (!select) return;
    if (select.options.length <= 1) {
      select.closest('[data-cod-block]')?.setAttribute('hidden', '');
    }
  }

  #attachCityDatalist() {
    const input = this.querySelector('input[name="city"]');
    if (!input || !input.dataset.codCities) return;
    const id = 'cod-cities-' + (input.dataset.codCitiesId || 'x');
    let list = document.getElementById(id);
    if (!list) {
      list = document.createElement('datalist');
      list.id = id;
      this.appendChild(list);
    }
    if (list.options.length < MA_CITIES.length) {
      list.innerHTML = '';
      for (const city of MA_CITIES) {
        const opt = document.createElement('option');
        opt.value = city;
        list.appendChild(opt);
      }
    }
    input.setAttribute('list', id);
  }

  /* ---------- items ---------- */

  async #refreshCart() {
    this.#attachCityDatalist();
    if (!this.#presetVariant) {
      const card = this.querySelector('input[data-cod-package]:checked');
      if (card) this.#presetVariant = card.value;
    }
    if (this.#presetVariant) return this.#refreshSingleItem();
    const source = this.dataset.source || 'cart';
    if (source === 'product') {
      const attr = this.querySelector('[data-cod-single-item]');
      const variantId = attr?.dataset.codSingleItem;
      if (variantId) this.#items = [{ variantId, quantity: 1, title: attr.dataset.codTitle || '', price: Number(attr.dataset.codPrice || '0') * 100, image: attr.dataset.codImage || '' }];
      this.#renderSummary();
      return;
    }
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.#items = (cart.items || []).map((l) => ({
        variantId: `gid://shopify/ProductVariant/${l.variant_id}`,
        quantity: l.quantity,
        title: l.product_title + (l.variant_title ? ` — ${l.variant_title}` : ''),
        price: l.final_line_price,
        image: l.image ? `https:${l.image}` : '',
      }));
      this.#renderCartList();
      this.#renderSummary();
    } catch {
      this.#items = [];
      this.#showError('form', this.#config.labels.empty_cart);
    }
  }

  async #refreshSingleItem() {
    const variantId = this.#presetVariant;
    if (!variantId) return;
    const num = variantId.split('/').pop();
    const card =
      this.querySelector('input[data-cod-package]:checked') ||
      this.querySelector(`input[data-cod-package][value="${variantId}"]`);
    const packLabel = card?.dataset.codPackLabel || '';
    const qty = Math.max(1, Number(card?.dataset.codQty || '1') || 1);
    const presetPrice = Number(card?.dataset.codPackPrice || '0');
    if (presetPrice > 0) {
      this.#items = [
        {
          variantId,
          quantity: qty,
          title: packLabel,
          price: Math.round(presetPrice),
          image: card?.dataset.codImage || '',
        },
      ];
      this.#renderCartList();
      this.#renderSummary();
      return;
    }
    try {
      const res = await fetch(`/variants/${num}.js`, { headers: { Accept: 'application/json' } });
      const p = await res.json();
      this.#items = [
        {
          variantId,
          quantity: qty,
          title: packLabel || p.title,
          price: Math.round(p.price) * qty,
          image: p.featured_image ? `https:${p.featured_image}` : '',
        },
      ];
    } catch {
      this.#items = [{ variantId, quantity: qty, title: packLabel, price: 0, image: '' }];
    }
    this.#renderCartList();
    this.#renderSummary();
  }

  #renderCartList() {
    const list = this.querySelector('[data-cod-cart-items]');
    if (!list) return;
    const empty = this.querySelector('[data-cod-cart-empty]');
    list.innerHTML = '';
    if (!this.#items?.length) {
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    for (const item of this.#items) {
      const li = document.createElement('li');
      li.className = 'cod-cart__item';
      const img = item.image ? `<img class="cod-cart__image" src="${item.image}" alt="" width="48" height="48" loading="lazy">` : '';
      li.innerHTML = `${img}<span class="cod-cart__title"></span><span class="cod-cart__qty">×${item.quantity}</span><span class="cod-cart__price"></span>`;
      li.querySelector('.cod-cart__title').textContent = item.title;
      li.querySelector('.cod-cart__price').textContent = this.#money(item.price);
      list.appendChild(li);
    }
  }

  /* ---------- summary ---------- */

  #rate() {
    const checked = this.querySelector('input[data-cod-rate]:checked') || this.querySelector('input[data-cod-rate]');
    if (!checked) return { id: 'free', name: '', price: 0 };
    return {
      id: checked.dataset.codRateId,
      name: checked.dataset.codRateName || '',
      price: Math.round(Number(checked.dataset.codRatePrice || '0') * 100),
    };
  }

  #fee(subtotal) {
    const el = this.querySelector('[data-cod-fee-value]');
    if (!el || el.dataset.codFeeEnabled !== 'true') return 0;
    const value = Number(el.dataset.codFeeValue || '0');
    if (!value) return 0;
    return el.dataset.codFeeType === 'percent' ? Math.round((subtotal * value) / 100) : Math.round(value * 100);
  }

  #renderSummary() {
    const firstRate = this.querySelector('input[data-cod-rate]');
    if (firstRate && !this.querySelector('input[data-cod-rate]:checked')) firstRate.checked = true;
    const ratesWrap = this.querySelector('[data-cod-hide-single="true"]');
    if (ratesWrap && ratesWrap.querySelectorAll('input[data-cod-rate]').length <= 1) {
      ratesWrap.closest('[data-cod-block]')?.setAttribute('hidden', '');
    }
    const subtotal = (this.#items || []).reduce((sum, i) => sum + i.price, 0);
    const rate = this.#rate();
    const fee = this.#fee(subtotal);
    const total = subtotal + rate.price + fee;
    const set = (sel, text) => {
      const el = this.querySelector(sel);
      if (el) {
        el.textContent = text;
        el.closest('[data-cod-summary-row]')?.removeAttribute('hidden');
      }
    };
    set('[data-cod-summary-subtotal]', this.#money(subtotal));
    const freeShippingText = this.#config?.locale === 'fr' ? 'Gratuite - 0 DH' : 'مجاني - 0 DH';
    set('[data-cod-summary-shipping]', rate.price ? this.#money(rate.price) : freeShippingText);
    if (fee > 0) set('[data-cod-summary-fee]', this.#money(fee));
    set('[data-cod-summary-total]', this.#money(total));
    return { subtotal, fee, total };
  }

  #money(cents) {
    const amount = cents / 100;
    const currency = this.#config?.currency || 'MAD';
    return `${amount.toFixed(2)} ${currency}`;
  }

  #formatPackPrices() {
    for (const el of this.querySelectorAll('[data-cod-pack-price]')) {
      el.textContent = this.#money(Number(el.dataset.codPackPrice || 0));
    }
    for (const el of this.querySelectorAll('[data-cod-pack-old]')) {
      el.textContent = this.#money(Number(el.dataset.codPackOld || 0));
    }
  }

  /* ---------- validation + submit ---------- */

  #clearErrors() {
    for (const el of this.querySelectorAll('[data-cod-error]')) el.hidden = true;
  }

  #showError(field, message) {
    const el = this.querySelector(`[data-cod-error="${field}"]`);
    if (el) {
      el.textContent = message;
      el.hidden = false;
    } else if (field !== 'form') {
      this.#showError('form', message);
    }
  }

  #validate() {
    this.#clearErrors();
    let ok = true;
    for (const input of this.querySelectorAll('[data-cod-required]')) {
      if (!String(input.value || '').trim()) {
        input.closest('[data-cod-block]')?.classList.add('cod-block--invalid');
        this.#showError(input.name.split(':')[0] === 'name' ? 'name' : input.name, this.#config.labels.required);
        ok = false;
      } else {
        input.closest('[data-cod-block]')?.classList.remove('cod-block--invalid');
      }
    }
    const phoneInput = this.querySelector('input[name="phone"]');
    if (phoneInput && String(phoneInput.value || '').trim()) {
      const cleaned = String(phoneInput.value).replace(/[\s.\-()]/g, '');
      const validMaPhone = /^(?:0[5-7]\d{8}|(?:\+212|00212|212)[5-7]\d{8})$/.test(cleaned);
      if (!validMaPhone) {
        phoneInput.closest('[data-cod-block]')?.classList.add('cod-block--invalid');
        this.#showError('phone', this.#config.labels.invalid_phone);
        ok = false;
      }
    }
    return ok;
  }

  #buildPayload() {
    const value = (sel) => this.querySelector(sel)?.value?.trim() || '';
    const checked = (sel) => this.querySelector(sel)?.checked || false;
    const customFields = [];
    for (const el of this.querySelectorAll('[name^="custom:"], [name^="check:"]')) {
      const prefix = el.name.startsWith('check:') ? 'check:' : 'custom:';
      const key = el.name.slice(prefix.length);
      let val = prefix === 'check:' ? (el.checked ? 'oui' : '') : String(el.value || '').trim();
      if (prefix === 'check:' && el.dataset.codValue) val = el.checked ? el.dataset.codValue : '';
      if (!val) continue;
      customFields.push({ key, label: el.dataset.codLabel || key, value: val });
    }
    const rate = this.#rate();
    const provinceSelect = this.querySelector('select[name="province"]');
    const provinceCode = provinceSelect?.value || '';
    const provinceName = provinceSelect?.selectedOptions?.[0]?.textContent?.trim() || '';
    if (!this.#idemKey) {
      this.#idemKey = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    if (!this.#eventId) {
      this.#eventId = crypto.randomUUID ? crypto.randomUUID() : this.#idemKey;
    }
    return {
      v: 2,
      locale: this.#config.locale === 'fr' ? 'fr' : 'ar',
      pageUrl: `${location.origin}${location.pathname}`,
      idempotencyKey: this.#idemKey,
      eventId: this.#eventId,
      items: (this.#items || []).map(({ variantId, quantity }) => ({ variantId, quantity })),
      shippingRateId: rate.id,
      customer: {
        name: value('[name="name"]'),
        phone: value('[name="phone"]'),
        ...(value('[name="email"]') ? { email: value('[name="email"]') } : {}),
        address1: value('[name="address1"]'),
        ...(value('[name="address2"]') ? { address2: value('[name="address2"]') } : {}),
        city: value('[name="city"]'),
        ...(provinceCode ? { provinceCode, provinceName } : {}),
        ...(value('[name="note"]') ? { note: value('[name="note"]') } : {}),
        countryCode: 'MA',
      },
      customFields,
      utm: this.#utm(),
      website: value('[name="website"]'),
    };
  }

  #utm() {
    const params = new URLSearchParams(location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'];
    const utm = {};
    for (const k of keys) {
      const v = params.get(k);
      if (v) utm[k] = v;
    }
    return utm;
  }

  async #submit() {
    if (this.#submitting) return;
    if (!this.#validate()) return;
    if (!this.#items || !this.#items.length) {
      this.#showError('form', this.#config.labels.empty_cart);
      return;
    }

    const subtotal = (this.#items || []).reduce((sum, i) => sum + i.price, 0);
    const { total } = this.#renderSummary();
    if (this.#config.minTotal > 0 && total / 100 < this.#config.minTotal) {
      this.#showError('form', this.#config.labels.form_error);
      return;
    }
    if (this.#config.maxTotal > 0 && total / 100 > this.#config.maxTotal) {
      this.#showError('form', this.#config.labels.form_error);
      return;
    }

    this.#submitting = true;
    const button = this.querySelector('[data-cod-submit]');
    const label = this.querySelector('[data-cod-submit-label]');
    if (button) button.disabled = true;
    if (label) label.textContent = this.#config.labels.sending;
    this.#showError('form', '');
    this.#track('submit', { value: total / 100, currency: this.#config.currency });

    let controller = null;
    try {
      controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      const payload = this.#buildPayload();
      const res = await fetch(this.#config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.ok) {
        if (body?.duplicate) { this.#finishDuplicate(body, payload); return; }
        const message = body?.message || this.#config.labels.form_error;
        this.#showError(body?.field || 'form', message);
        return;
      }
      if (body.duplicate) { this.#finishDuplicate(body, payload); return; }
      if (!body.orderName) {
        this.#showError('form', this.#config.labels.form_error);
        return;
      }
      this.#idemKey = null;
      this.#eventId = null;
      this.#lastOrder = body;
      if (this.#isUrlThankYou()) {
        this.#redirectToThankYou(body, payload);
        return;
      }
      this.#purchase(body, payload.eventId);
      this.#showSuccess(body.orderName, body.total);
    } catch {
      this.#showError('form', this.#config.labels.form_error);
      const wa = this.querySelector('[data-cod-whatsapp]');
      if (wa) wa.hidden = false;
    } finally {
      this.#submitting = false;
      if (button) button.disabled = false;
      if (label) label.textContent = label.dataset.labelIdle || label.textContent;
    }
  }

  #finishDuplicate(body, payload) {
    if (this.#isUrlThankYou()) {
      this.#redirectToThankYou(body, payload, { duplicate: true });
      return;
    }
    this.#showError('form', this.#config.labels.duplicate);
  }

  /* ---------- pixels ---------- */

  #pushEvent(name, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...params });
    } catch { /* pixel failures never block orders */ }
  }

  #track(name, params) {
    const px = this.#config.pixels || {};
    const payload = { ...params, currency: this.#config.currency };
    if (px.ga4) this.#pushEvent(name, payload);
    const pixelEvent = name === 'submit' ? 'InitiateCheckout' : name === 'begin_checkout' ? 'ViewContent' : null;
    if (!pixelEvent) return;
    try {
      if (px.fb && typeof window.fbq === 'function') window.fbq('track', pixelEvent, payload);
      if (px.tiktok && window.ttq && typeof window.ttq.track === 'function') window.ttq.track(pixelEvent, payload);
    } catch { /* pixel failures never block orders */ }
  }

  #purchase(body, eventId) {
    const px = this.#config.pixels || {};
    const value = Number(body.total || '0');
    const transactionId = body.orderName || eventId || '';
    const params = { transaction_id: transactionId, value, currency: this.#config.currency };
    if (px.ga4) this.#pushEvent(px.leadInsteadOfPurchase ? 'generate_lead' : 'purchase', params);
    if (px.ads && px.adsLabel) {
      this.#pushEvent('conversion', {
        send_to: `${px.ads}/${px.adsLabel}`,
        transaction_id: transactionId,
        value,
        currency: this.#config.currency,
      });
    }
    try {
      if (px.fb && typeof window.fbq === 'function') {
        window.fbq('track', px.leadInsteadOfPurchase ? 'Lead' : 'Purchase', params, { eventID: eventId || transactionId });
      }
      if (px.tiktok && window.ttq && typeof window.ttq.track === 'function') {
        window.ttq.track(px.leadInsteadOfPurchase ? 'SubmitForm' : 'CompletePayment', params, { event_id: eventId || transactionId });
      }
    } catch { /* pixel failures never block orders */ }
  }

  /* ---------- sticky + hide flags ---------- */

  #syncCheckoutVisibility() {
    if (this.dataset.hideAtc === 'true') document.body.classList.add('cod-hide-atc');
    if (this.dataset.hideCheckout === 'true') document.body.classList.add('cod-hide-checkout');
  }
}

if (!customElements.get('cod-order-form')) {
  customElements.define('cod-order-form', CodOrderForm);
}
